import { Component, computed, inject, Signal } from '@angular/core';
import { DividerComponent } from '@lb/components/divider/divider.component';
import { format, parse, parseISO } from 'date-fns';
import { MDContentBoxComponent, MDIconComponent } from '@melodic-ui/components';
import { SchedulerFormService, ScheduleTimeSlot } from '@lb/schedule/services/scheduler-form.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, startWith, switchMap } from 'rxjs';
import { DayOfWeek } from '@lb/api/mylb/interfaces/ienrollment-schedule.interface';
import { FormFieldComponent } from '@lb/components/form/form-field/form-field.component';
import { SchedulerStateService } from '@lb/schedule/services/scheduler-state.service';
import { GlobalTimezoneService } from '@lb/services/global-timezone.service';

@Component({
	selector: 'lb-schedule-review',
	standalone: true,
	imports: [MDIconComponent, MDContentBoxComponent, DividerComponent, FormFieldComponent],
	templateUrl: './schedule-review.component.html',
	styleUrl: './schedule-review.component.scss'
})
export class ScheduleReviewComponent {
	private readonly _schedulerFormService = inject(SchedulerFormService);
	private readonly _schedulerStateService = inject(SchedulerStateService);
	private readonly _globalTimezoneService = inject(GlobalTimezoneService);

	private readonly _scheduleForm2Signal = toSignal(
		toObservable(this._schedulerFormService.step2Form).pipe(
			filter((form) => !!form),
			switchMap((form) => form.valueChanges.pipe(startWith(form.value)))
		),
		{ initialValue: undefined }
	);

	private readonly _scheduleForm3Signal = toSignal(
		toObservable(this._schedulerFormService.step3Form).pipe(
			filter((form) => !!form),
			switchMap((form) => form.valueChanges.pipe(startWith(form.value)))
		),
		{ initialValue: undefined }
	);

	enrollmentNotes = computed(() => {
		const notes = this._schedulerStateService.enrollmentNotes();

		if (!notes || notes.length === 0) {
			return null;
		}

		return {
			languageExperience: notes.find((x) => x.noteTypeID === 1)?.note || null,
			learningNeeds: notes.find((x) => x.noteTypeID === 2)?.note || null,
			requestedInstructor: notes.find((x) => x.noteTypeID === 4)?.note || null
		};
	});

	readonlyLanguageExperience = computed(() => this.enrollmentNotes()?.languageExperience || '');
	readonlyLearningNeeds = computed(() => this.enrollmentNotes()?.learningNeeds || '');
	readonlyRequestedInstructor = computed(() => this.enrollmentNotes()?.requestedInstructor || '');

	preferredTimeSlotSelections = computed<{ day: string; times: string[] }[]>(() => {
		const selectedSchedules = this._scheduleForm2Signal();

		return this.mapTimeSlots(selectedSchedules || []);
	});

	timeSlotSelections = computed<{ day: string; times: string[] }[]>(() => {
		const selectedSchedules = this._scheduleForm3Signal();

		return this.mapTimeSlots(selectedSchedules || []);
	});

	lessonsFrequency = computed(() => {
		const formData = this._schedulerFormService.getFormData();
		const currentEnrollment = this._schedulerStateService.enrollment();
		return formData.configuration?.frequency || currentEnrollment?.lesson?.lessonFrequency || 0;
	});

	startDate = computed(() => {
		const formData = this._schedulerFormService.getFormData();
		const currentEnrollment = this._schedulerStateService.enrollment();

		const formStartDate = formData.configuration?.startDate;
		const enrollmentStartDate = currentEnrollment?.lesson?.startDate;
		const startDateToUse = formStartDate || enrollmentStartDate;

		if (startDateToUse) {
			const parsedStartDate = typeof startDateToUse === 'string' ? parseISO(startDateToUse) : startDateToUse;
			return format(parsedStartDate, 'd MMM yyyy');
		}
		return '';
	});

	endDate = computed(() => {
		const currentEnrollment = this._schedulerStateService.enrollment();
		const anticipatedEndDateString = currentEnrollment?.lesson?.anticipatedEndDate;

		if (anticipatedEndDateString) {
			try {
				const parsedAnticipatedEndDate =
					typeof anticipatedEndDateString === 'string' ? parseISO(anticipatedEndDateString) : new Date(anticipatedEndDateString);
				return format(parsedAnticipatedEndDate, 'd MMM yyyy');
			} catch {
				return 'To be determined';
			}
		}
		return 'To be determined';
	});

	timeZoneName: Signal<string> = computed(() => {
		const formData = this._schedulerFormService.getFormData();
		return this._globalTimezoneService.availableTimezones().find((tz) => tz.timeZoneID === formData.configuration?.timeZone)?.timeZoneName ?? '';
	});

	languageExperience = computed(() => {
		const formData = this._schedulerFormService.getFormData();
		return formData.reviewData?.languageExperience || null;
	});

	learningNeeds = computed(() => {
		const formData = this._schedulerFormService.getFormData();
		return formData.reviewData?.learningNeeds || null;
	});

	requestedInstructor = computed(() => {
		const formData = this._schedulerFormService.getFormData();
		return formData.reviewData?.requestedInstructor || null;
	});

	updateReviewField = (field: 'languageExperience' | 'learningNeeds' | 'requestedInstructor', event: any | null) => {
		const text = event.target.value;
		const form = this._schedulerFormService.step4Form();

		if (form) {
			form.get(field)?.setValue(text);
		}
	};

	private mapTimeSlots(selectedSchedules: ScheduleTimeSlot[]): { day: string; times: string[] }[] {
		if (selectedSchedules === undefined || selectedSchedules.length === 0) {
			return [];
		}

		const enrollmentSchedules = this._schedulerFormService.mergeBlocksToSchedules(selectedSchedules);
		// Group by dayIndex
		const grouped: Record<number, string[]> = {};
		enrollmentSchedules.forEach((schedule) => {
			const timeRange = `${format(parse(schedule.startTime, 'HH:mm', new Date()), 'h:mm a')} - ${format(parse(schedule.endTime, 'HH:mm', new Date()), 'h:mm a')}`;
			if (!grouped[schedule.dayIndex]) grouped[schedule.dayIndex] = [];
			grouped[schedule.dayIndex].push(timeRange);
		});

		// Map to array with day name and times
		return Object.entries(grouped).map(([dayIndex, times]) => ({
			day: DayOfWeek[+dayIndex].shortName,
			times
		}));
	}
}
